import React, { useState } from "react";
import { toast } from "react-toastify";
import { addBalance, debitBalance } from "../../api/accountsApi";
import { getErrorMessage } from "../../utils/network";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/** Add money to, or debit money from, an account's wallet (needs the caller's wallet password). */
const WalletModal = ({ mode, account, user, onClose, onDone }) => {
  const debit = mode === "debit";
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const wallet = Number(account.wallet || 0);
  const amt = Number(amount);
  const after = debit ? wallet - (amt || 0) : wallet + (amt || 0);
  const tooMuch = debit && amt > wallet;

  const submit = async () => {
    if (!amount || Number.isNaN(amt) || amt <= 0) {
      toast.warn("Enter a valid amount.");
      return;
    }
    if (tooMuch) {
      toast.warn(`The wallet has only ${rupees(wallet)}.`);
      return;
    }
    if (debit && !remarks.trim()) {
      toast.warn("Enter the reason for the debit.");
      return;
    }
    if (!pw) {
      toast.warn("Enter your wallet password to confirm.");
      return;
    }
    setBusy(true);
    try {
      const body = { id: account.id, amount: amt, remarks: remarks.trim(), walletPassword: pw };
      if (!debit) body.added_by = `${user.operatorName || ""}, ${user.phoneNumber || ""}`;
      const { data } = await (debit ? debitBalance(body) : addBalance(body));
      if (data.status !== "success") throw new Error(data.error || "Failed");
      toast.success(debit ? "Wallet debited" : "Balance added");
      onDone && onDone();
    } catch (err) {
      toast.error(getErrorMessage(err, err.message || "Failed to update the wallet"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acc-overlay" role="dialog" aria-modal="true" aria-label={debit ? "Debit wallet" : "Add balance"}>
      <div className="acc-modal acc-modal--narrow">
        <div className="acc-modal__head">
          <h3>{debit ? "Debit wallet" : "Add balance"} — {account.operatorName || account.username}</h3>
          <button type="button" className="acc-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="acc-modal__body">
          <div className="acc-wallet-line">
            <span>Current wallet</span><strong>{rupees(wallet)}</strong>
          </div>
          <label className="acc-f">
            <span>Amount (₹)</span>
            <input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          </label>
          <label className="acc-f">
            <span>{debit ? "Reason" : "Remarks"}</span>
            <input value={remarks} onChange={(e) => setRemarks(e.target.value)}
              placeholder={debit ? "e.g. Wrong top-up on 01 Nov" : "Optional"} />
          </label>
          <label className="acc-f">
            <span>Your wallet password</span>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" />
          </label>
          {amt > 0 && (
            <div className={`acc-wallet-line ${tooMuch ? "is-bad" : ""}`}>
              <span>Wallet after</span><strong>{tooMuch ? "Not enough balance" : rupees(after)}</strong>
            </div>
          )}
          <p className="acc-hint">No wallet password yet? Set one under Change Password.</p>
        </div>
        <div className="acc-modal__foot">
          <button type="button" className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className={`acc-btn ${debit ? "acc-btn--danger" : "acc-btn--primary"}`} onClick={submit} disabled={busy || tooMuch}>
            {busy ? "Saving…" : debit ? "Debit" : "Add balance"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalletModal;
